// src/components/cards/ClientDetailsCard.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Popper,
  Paper,
  ClickAwayListener,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  WarningAmber as WarningIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  LocalHospital as VetIcon,
  Person as PersonIcon,
  Description as NotesIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  AccountBalance as BalanceIcon,
  Check as TrueIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Client, invoiceAPI, AccountSummary } from '../../services/api';
import { formatCurrency } from '../lists/AccountsList/accountTypes';
import { ResponsiveFormGrid } from '../forms/shared';
import { FloatingAccessButton } from '../common/FloatingAccessButton';
import { NotProvided } from '../common/NotProvided';
import {
  BeingDetailsField,
  BeingHeaderField,
  MetadataField,
} from '../forms/shared/DisplayFieldVariants';
import { SpacerRow } from '../forms/shared/SpacerRow';
import { CardFooterContainer, CardFooter } from './shared/CardElements';
import { getCustomIcon } from '../../utils/customIcons';
import BottomMenu, { BottomMenuItem } from '../menus/BottomMenu';
import { useHeaderDrawer } from '../../contexts/HeaderDrawerContext';
import { useAnchorRect } from '../../hooks/useAnchorRect';

interface ClientDetailsCardProps {
  client: Client;
}

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const StyledWarningIcon = styled(WarningIcon)(({ theme }) => ({
  fontSize: '3rem',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.light
      : theme.palette.secondary.main,
  marginLeft: '1.5rem',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.secondary.light
        : theme.palette.secondary.dark,
    transform: 'scale(1.1)',
  },
}));

const PopperContent = styled(Paper)(({ theme }) => ({
  padding: '12px 16px',
  maxWidth: '400px',
  backgroundColor: theme.palette.background.paper,
  border: '3px solid',
  borderColor:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.main
      : theme.palette.secondary.main,
  borderRadius: '8px',
  boxShadow: theme.shadows[8],
  zIndex: 1300,
}));

const getClientMetadataValue = (client: Client) => {
  const createdDate = client.createdAt
    ? new Date(client.createdAt).toLocaleString()
    : 'Unknown';

  const createdAtISO = client.createdAt
    ? new Date(client.createdAt).toISOString()
    : null;
  const updatedAtISO = client.updatedAt
    ? new Date(client.updatedAt).toISOString()
    : null;

  const hasUpdate =
    updatedAtISO && createdAtISO && updatedAtISO !== createdAtISO;

  const createdLine = client.createdByUsername
    ? `Created: ${createdDate} by ${client.createdByUsername}`
    : `Created: ${createdDate}`;

  if (hasUpdate && client.updatedAt) {
    const updatedDate = new Date(client.updatedAt).toLocaleString();
    const updatedLine = client.updatedByUsername
      ? `Updated: ${updatedDate} by ${client.updatedByUsername}`
      : `Updated: ${updatedDate}`;
    return (
      <Box
        sx={{
          whiteSpace: 'nowrap',
          overflowX: 'auto',
          overflowY: 'visible',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {createdLine}
        <br />
        {updatedLine}
      </Box>
    );
  }

  return (
    <Box component="span" sx={{ display: 'block', padding: '0.75em 0' }}>
      <Box
        sx={{
          whiteSpace: 'nowrap',
          overflowX: 'auto',
          overflowY: 'visible',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {createdLine}
      </Box>
    </Box>
  );
};

const ClientDetailsCard: React.FC<ClientDetailsCardProps> = ({ client }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isHeaderDrawerOpen } = useHeaderDrawer();

  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const [popperOpen, setPopperOpen] = useState(false);
  const [bottomMenuOpen, setBottomMenuOpen] = useState(false);
  const bottomMenuFabRef = useRef<HTMLDivElement>(null);
  const bottomMenuAnchor = useAnchorRect(bottomMenuFabRef, bottomMenuOpen);
  const [accountSummary, setAccountSummary] = useState<AccountSummary | null>(
    null
  );

  const loadAccountSummary = useCallback(async () => {
    try {
      const summary = await invoiceAPI.getAccountSummary(client.id);
      setAccountSummary(summary);
    } catch {
      // Silent - balance simply won't display
    }
  }, [client.id]);

  useEffect(() => {
    loadAccountSummary();
  }, [loadAccountSummary]);

  const getBalanceDisplay = (): React.ReactNode => {
    if (!accountSummary) return null;
    const balance = accountSummary.outstandingBalance;
    const isCredit = balance < 0;
    const isZero = balance === 0;

    const text = isZero
      ? 'R 0.00'
      : isCredit
        ? `${formatCurrency(Math.abs(balance))} CR`
        : `${formatCurrency(balance)} DUE`;

    const color = isZero
      ? theme.palette.text.primary
      : isCredit
        ? 'inherit'
        : 'inherit';

    return (
      <Typography
        component="span"
        sx={{ color, fontWeight: 600, fontSize: 'inherit' }}
      >
        {text}
      </Typography>
    );
  };

  const balanceDisplay = getBalanceDisplay();
  const isBalanceZero = accountSummary?.outstandingBalance === 0;

  const cardFooterProps = {
    borderTop: `3px solid ${theme.palette.grey[900]}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[600],
  };

  const getVetDisplayName = (): React.ReactNode => {
    if (client.vet) {
      const vetName = client.vet.name || 'Unknown name';
      const vetPhone = client.vet.phone || 'No phone';
      return `${vetName} - ${vetPhone}`;
    } else if (client.vetId) {
      return `Vet ID: ${client.vetId} (vet data not loaded - backend fix needed)`;
    } else {
      return <NotProvided type="NotProvided" />;
    }
  };

  const handleWarningMouseEnter = (event: React.MouseEvent<SVGSVGElement>) => {
    setAnchorEl(event.currentTarget);
    setPopperOpen(true);
  };

  const handleWarningMouseLeave = () => {
    setPopperOpen(false);
  };

  const handleClickAway = () => {
    setPopperOpen(false);
  };

  const handleEditClick = () => {
    navigate(`/clients/${client.id}/edit`);
  };

  const clientMenuItems: BottomMenuItem[] = [
    {
      key: 'new-booking',
      label: 'New Booking',
      onClick: () => navigate(`/clients/${client.id}/newbooking`),
    },
    {
      key: 'bookings',
      label: 'Bookings',
      onClick: () => navigate(`/clients/${client.id}/bookings`),
    },
    {
      key: 'new-quote',
      label: 'New Quote',
      onClick: () => navigate(`/clients/${client.id}/quote/new`),
    },
    {
      key: 'quotes',
      label: 'Quotes',
      onClick: () => navigate(`/clients/${client.id}/quotations`),
    },
    {
      key: 'new-pet',
      label: 'New Pet',
      onClick: () => navigate(`/clients/${client.id}/newpet`),
    },
    {
      key: 'pets',
      label: 'Pets',
      onClick: () => navigate(`/clients/${client.id}/pets`),
    },
    {
      key: 'account',
      label: 'Account',
      onClick: () => navigate(`/clients/${client.id}/account`),
    },
    {
      key: 'edit',
      label: `Edit ${client.firstName}`,
      onClick: handleEditClick,
    },
  ];

  const getClientNameAndWarningIcon = (client: Client) => {
    return (
      <Box display="flex" alignItems="center">
        <Typography
          sx={{
            fontSize: '2rem',
            fontWeight: 500,
            wordBreak: 'break-word',
            minHeight: '2.4rem',
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.text.primary,
            paddingLeft: '1rem',
          }}
        >
          {client.firstName || 'Not provided'}
        </Typography>
        {client.showWarning && (
          <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ display: 'flex', ml: 1 }}>
              <StyledWarningIcon
                onMouseEnter={handleWarningMouseEnter}
                onMouseLeave={handleWarningMouseLeave}
                aria-label="Warning - hover for details"
              />
              <Popper
                open={popperOpen}
                anchorEl={anchorEl}
                placement="bottom"
                modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
              >
                <PopperContent>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      fontSize: '2rem',
                      color: theme.palette.text.primary,
                      lineHeight: 1.4,
                    }}
                  >
                    {client.notes || 'No warning notes provided'}
                  </Typography>
                </PopperContent>
              </Popper>
            </Box>
          </ClickAwayListener>
        )}
      </Box>
    );
  };

  return (
    <>
      <CardFooterContainer sx={{ minHeight: 'calc(100vh - 220px)' }}>
        <ResponsiveFormGrid
          alternateRowColors
          breakpointPadding={{
            default: '0',
            medium: '0',
            large: '0',
          }}
          container
          spacing={0}
        >
          {!isMobile && (
            <>
              <BeingHeaderField
                label="Name"
                value={getClientNameAndWarningIcon(client)}
                hideColon
                labelFontWeight={700}
              />
              <BeingHeaderField
                label="Surname"
                value={client.lastName || <NotProvided type="NotProvided" />}
                valuePadding="2.4px 0 2.4px 1rem"
                hideColon
                labelFontWeight={700}
              />
            </>
          )}

          <BeingDetailsField
            label="Telephone"
            value={client.phone || ''}
            valuePadding="2.4px 0 2.4px 1rem"
            startIcon={<PhoneIcon />}
            copyable
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Email"
            value={client.email || ''}
            valuePadding="2.4px 0 2.4px 1rem"
            startIcon={<EmailIcon />}
            copyable
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Address"
            valuePadding="2.4px 0 2.4px 1rem"
            value={client.address || <NotProvided type="NotProvided" />}
            startIcon={<HomeIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Vet"
            valuePadding="2.4px 0 2.4px 1rem"
            value={getVetDisplayName()}
            startIcon={<VetIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Alt. Contact"
            valuePadding="2.4px 0 2.4px 1rem"
            value={
              client.alternateContact || <NotProvided type="NotProvided" />
            }
            startIcon={<PersonIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Alt. Contact Other"
            valuePadding="2.4px 0 2.4px 1rem"
            value={
              client.alternateContactOther || <NotProvided type="NotProvided" />
            }
            startIcon={<PersonIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          {client.notes && client.notes.trim() && (
            <BeingDetailsField
              label="Notes"
              valuePadding="2.4px 0 2.4px 1rem"
              value={client.notes}
              startIcon={<NotesIcon />}
              hideColon
              labelFontWeight={700}
              valuePaddingMobile="2.4px 0"
            />
          )}

          {client.createdFromQuoteId && !client.isProspect && (
            <BeingDetailsField
              label="Converted"
              valuePadding="2.4px 0 2.4px 1rem"
              value={
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    navigate(`/quote/${client.createdFromQuoteId}`)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/quote/${client.createdFromQuoteId}`);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    '&:hover': { textDecoration: 'underline' },
                    whiteSpace: 'nowrap',
                    overflowX: 'auto',
                    overflowY: 'visible',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    display: 'block',
                    width: '98%',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {isMobile ? 'Converted ' : ''}
                  {`from quote #${client.createdFromQuoteId}`}
                  {client.createdFromQuoteConvertedAt
                    ? ` on ${formatDate(client.createdFromQuoteConvertedAt)}`
                    : ''}
                </Box>
              }
              startIcon={getCustomIcon('convert', '2.4rem')}
              hideColon
              labelFontWeight={700}
              valuePaddingMobile="2.4px 0"
            />
          )}

          <BeingDetailsField
            label="Account Balance"
            valuePadding="2.4px 0 2.4px 1rem"
            value={
              isBalanceZero
                ? 'R 0.00'
                : balanceDisplay || <NotProvided type="NotProvided" />
            }
            startIcon={<BalanceIcon />}
            endIcon={isBalanceZero ? <TrueIcon /> : undefined}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <SpacerRow
            height=".1rem"
            labelHeightMobile="0"
            valueHeightMobile="1rem"
            labelBackgroundColor={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[800]
                : theme.palette.grey[400]
            }
            labelBackgroundColorMobile={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[750]
                : theme.palette.grey[300]
            }
          />

          <Box sx={{ width: '100%', position: 'relative' }}>
            <MetadataField
              label={`Client #${client.id}`}
              labelFontSize="2rem"
              labelFontSizeMobile="1.8rem"
              labelFontWeight={700}
              valueFontSizeMobile="1.4rem"
              value={getClientMetadataValue(client)}
              valuePaddingMobile="0.5rem 1.5rem 5rem 1.5rem"
            />
            {!isHeaderDrawerOpen && (
              <FloatingAccessButton
                ref={bottomMenuFabRef}
                position="absolute"
                icon={bottomMenuOpen ? <CloseIcon /> : <MenuIcon />}
                hoverIcon={bottomMenuOpen ? <CloseIcon /> : <MenuIcon />}
                onClick={() => setBottomMenuOpen((v) => !v)}
                iconScale={2}
                aria-label={
                  bottomMenuOpen ? 'Close actions menu' : 'Open actions menu'
                }
                backgroundColor={
                  bottomMenuOpen
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                hoverBackgroundColor={
                  bottomMenuOpen
                    ? theme.palette.primary.dark
                    : theme.palette.secondary.dark
                }
                bottom="2rem"
                right="10%"
                sx={{
                  zIndex: 1300,
                  border: '2px solid #fff',
                  '@media (max-width: 600px)': {
                    position: 'fixed',
                    bottom: '1rem',
                    right: '1rem',
                    '& svg': {
                      transform: 'scale(1.2)',
                    },
                  },
                  '@media (min-width: 601px)': {
                    width: '75px',
                    height: '75px',
                    minWidth: '75px',
                    minHeight: '75px',
                    top: 'calc(50% - 37.5px)',
                  },
                  '& svg': {
                    transition: 'transform 0.3s ease-in-out',
                    transform: 'scale(1.4)',
                  },
                  '&:active': {
                    backgroundColor: bottomMenuOpen
                      ? 'secondary.light' // Color when clicking to close
                      : 'primary.dark', // Color when clicking to open
                  },
                  '&:hover svg': {
                    opacity: 0.8,
                    transform: bottomMenuOpen
                      ? 'scale(2.1) rotate(-180deg)' // Counter-clockwise on close
                      : 'scale(2.1) rotate(180deg)', // Clockwise on open
                  },
                  // Approximate alignment with the EDIT tab in BeingsPageSubMenu.
                  // EDIT sits at the sum of preceding tab minWidths (clientdetails
                  // 266 + newbooking 250 + bookings 208 + newquotation 212 +
                  // quotations 154 + newpet 176 + pets 110 + account 178 = 1554),
                  // centred over its own 110px width. Update if those tab widths
                  // change. Only applies once the tab row fits without scrolling.
                  '@media (min-width: 1700px)': {
                    left: '1609px',
                  },
                }}
              />
            )}
          </Box>
        </ResponsiveFormGrid>

        <CardFooter {...cardFooterProps} />
      </CardFooterContainer>

      <BottomMenu
        open={bottomMenuOpen}
        onClose={() => setBottomMenuOpen(false)}
        items={clientMenuItems}
        right={bottomMenuAnchor ? `${bottomMenuAnchor.right}px` : '1rem'}
        bottom={bottomMenuAnchor ? `${bottomMenuAnchor.bottom}px` : '40px'}
        sx={{
          '@media (max-width: 900px)': {
            marginRight: '.1rem',
            marginBottom: '.1rem',
          },
          '@media (min-width: 900px)': {
            marginRight: '1rem',
            marginBottom: '1rem',
          },
        }}
      />
    </>
  );
};

export default ClientDetailsCard;
